import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, ShoppingCart, Wallet, Scale, PieChart,
  Receipt, BookOpen, UserCog, BadgeIndianRupee, FileBarChart2, FileText,
  LogOut, Menu, X, Store, ShieldCheck, Eye, History,
} from "lucide-react";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/suppliers", label: "Supplier Details", icon: Users },
  { to: "/purchase", label: "Purchase Entry", icon: ShoppingCart },
  { to: "/payment", label: "Payment Entry", icon: Wallet },
  { to: "/balance", label: "Balance Report", icon: Scale },
  { to: "/analysis", label: "Supplier Analysis", icon: PieChart },
  { to: "/expenses", label: "Expenses", icon: Receipt },
  { to: "/ledger", label: "Day Ledger", icon: BookOpen },
  { to: "/employees", label: "Employee Details", icon: UserCog },
  { to: "/salary", label: "Salary Details", icon: BadgeIndianRupee },
  { to: "/party-statement", label: "Party Statement", icon: FileText },
  { to: "/reports", label: "Reports", icon: FileBarChart2 },
  { to: "/users", label: "User Management", icon: ShieldCheck, adminOnly: true },
  { to: "/audit", label: "Audit Log", icon: History, adminOnly: true },
];

export default function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const navItems = NAV.filter((item) => !item.adminOnly || isAdmin);

  const SidebarInner = () => (
    <div className="flex h-full flex-col bg-sidebar text-sidebar-foreground">
      <div className="flex items-center gap-3 px-5 py-5 border-b border-white/10">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-sidebar-accent text-white shadow-lg">
          <Store className="h-5 w-5" />
        </div>
        <div>
          <div className="font-display text-lg font-bold text-white leading-none">SSACC</div>
          <div className="text-[11px] tracking-wide text-sidebar-foreground/70 mt-1">SARAVANA STORES</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto ssacc-scroll px-3 py-4 space-y-1">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={() => setOpen(false)}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                isActive
                  ? "bg-sidebar-accent text-white shadow-md"
                  : "text-sidebar-foreground/80 hover:bg-white/5 hover:text-white"
              )
            }
          >
            <item.icon className="h-[18px] w-[18px]" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 p-3">
        <button
          onClick={logout}
          data-testid="logout-button"
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-sidebar-foreground/80 hover:bg-white/5 hover:text-white transition-colors"
        >
          <LogOut className="h-[18px] w-[18px]" /> Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 z-30">
        <SidebarInner />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="relative w-64 z-50">
            <SidebarInner />
          </div>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex items-center justify-between gap-4 border-b border-border bg-card/80 backdrop-blur px-4 lg:px-8 py-3">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden rounded-md p-2 hover:bg-muted"
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <div>
              <h1 className="font-display text-lg font-bold text-foreground leading-none">
                Accounts Control Center
              </h1>
              <p className="text-xs text-muted-foreground mt-0.5">Suppliers · Payments · Payroll</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold text-foreground">{user?.name || "Admin"}</div>
              <div className="text-xs text-muted-foreground">{user?.email}</div>
            </div>
            {!isAdmin && (
              <span className="hidden sm:inline-flex items-center gap-1 rounded-full bg-accent/20 text-accent-foreground text-xs font-semibold px-2.5 py-1" data-testid="viewonly-badge">
                <Eye className="h-3 w-3" /> View Only
              </span>
            )}
            <div className="grid h-9 w-9 place-items-center rounded-full bg-primary text-primary-foreground text-sm font-bold">
              {(user?.name || "A").charAt(0)}
            </div>
          </div>
        </header>

        <main className="p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
